/**
 * TanStack Query hooks for Student resource (StudentProfile).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export type StudentStatus = 'active' | 'inactive' | 'graduated' | 'withdrawn';
export type Gender = 'male' | 'female' | 'other';

export interface Student {
  id: string;
  studentCode: string;
  branchId: string;
  branchName?: string;
  fullName: string;
  dateOfBirth: string;
  gender?: Gender | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  enrolledAt?: string | null;
  status: StudentStatus;
  note?: string | null;
  parentCount?: number;
  enrollmentCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListStudentsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  branchId?: string;
  status?: StudentStatus;
}

export const studentKeys = {
  all: ['students'] as const,
  list: (params?: ListStudentsParams) => ['students', 'list', params] as const,
  detail: (id: string) => ['students', 'detail', id] as const,
  enrollments: (id: string) => ['students', id, 'enrollments'] as const,
  parents: (id: string) => ['students', id, 'parents'] as const,
  attendance: (id: string, classId?: string) =>
    ['students', id, 'attendance', classId] as const,
};

export function useStudents(params?: ListStudentsParams) {
  return useQuery({
    queryKey: studentKeys.list(params),
    queryFn: () =>
      api.getPage<Student>('/api/students', params as Record<string, string>),
  });
}

export function useStudent(id: string) {
  return useQuery({
    queryKey: studentKeys.detail(id),
    queryFn: () => api.get<Student>(`/api/students/${id}`),
    enabled: !!id,
  });
}

export function useCreateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<Student>('/api/students', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

export function useUpdateStudent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<Student>(`/api/students/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: studentKeys.all });
      void qc.invalidateQueries({ queryKey: studentKeys.detail(id) });
    },
  });
}

export interface StudentEnrollmentHistory {
  id: string;
  classId: string;
  classCode?: string;
  className?: string;
  branchName?: string;
  status: string;
  enrolledAt?: string;
  completedAt?: string | null;
  droppedAt?: string | null;
}

export function useStudentEnrollments(studentId: string) {
  return useQuery({
    queryKey: studentKeys.enrollments(studentId),
    // No /api/students/:id/enrollments route — filter via main enrollments endpoint
    queryFn: () =>
      api.getPage<StudentEnrollmentHistory>(
        `/api/enrollments`,
        { studentId, pageSize: 100 },
      ),
    enabled: !!studentId,
  });
}

export interface ParentLink {
  id: string;
  parentId: string;
  parentName?: string;
  parentPhone?: string;
  relation: string;
  isPrimary: boolean;
}

export function useStudentParents(studentId: string) {
  return useQuery({
    queryKey: studentKeys.parents(studentId),
    // Backend returns { data: ParentLink[] } — api.get unwraps to ParentLink[]
    queryFn: () =>
      api.get<ParentLink[]>(`/api/students/${studentId}/parents`),
    enabled: !!studentId,
  });
}
